import { useCallback } from 'react';

import { useAuthStore } from 'src/auth/store/auth.store';
import { useMeStore } from 'src/store/me/me.store';
import { setSentryUser, clearSentryUser } from 'src/init-sentry';

import { isValidToken, setSession, getStoredTokens } from '../utils/utils';

export const useAuth = () => {
  const { setIsAuthenticated, setLoading, refreshAccessToken } = useAuthStore();

  const initializeAuth = useCallback(async () => {
    try {
      setLoading(true);

      const { accessToken, refreshToken } = getStoredTokens();

      if (accessToken && isValidToken(accessToken)) {
        // If access token is valid, log in the user
        setSession(accessToken, refreshToken);
        setIsAuthenticated(true);

        // Setar dados do usuário no Sentry
        const { me } = useMeStore.getState();
        if (me) {
          setSentryUser({
            id: me.id,
            email: me.email,
            name: me.displayName,
          });
        }
      } else if (refreshToken && isValidToken(refreshToken)) {
        // If refresh token is valid, try to refresh access token
        try {
          const newAccessToken = await refreshAccessToken();
          if (newAccessToken && isValidToken(newAccessToken)) {
            // If new access token is obtained, set authenticated state
            // Não precisa chamar setSession novamente, pois refreshAccessToken já faz isso
            setIsAuthenticated(true);
          } else {
            // If no valid tokens, clear session and set unauthenticated state
            setSession(null, null);
            setIsAuthenticated(false);
          }
        } catch (error) {
          console.error('Failed to refresh token:', error);
          // Não mostra snackbar na inicialização, apenas limpa a sessão
          setSession(null, null);
          setIsAuthenticated(false);
          clearSentryUser();
        }
      } else {
        // If no valid tokens, set unauthenticated state
        setSession(null, null);
        setIsAuthenticated(false);
        clearSentryUser();
      }
    } catch (error) {
      console.error('Auth initialization failed:', error);
      setSession(null, null);
      setIsAuthenticated(false);
      clearSentryUser();
    } finally {
      setLoading(false);
    }
  }, [setIsAuthenticated, setLoading, refreshAccessToken]);

  return {
    initializeAuth,
  };
};
